import type { IOfficeInfoWritePayload, IUpdateOfficeInfoApiPayload } from '~/domain/UpdateOffice';
import type { IUser } from '~/models/User';
import type {
  IBasicInformationValues,
  IContactValues,
  IOfficeProductivityPayload,
  IShiftDetailsValues,
  TStaffProfession,
} from '../types/LocationSettings';

/**
 * The `office_info` keys an office edits, taken from the stored record and
 * overlaid with the section's changes. Whether the monolith merges a partial
 * `office_info` is unconfirmed, so every write carries all five; server-owned
 * keys (ratings, terms, Best in Dental) are never echoed back.
 */
const editableOfficeInfo = (
  office: IUser,
  changes: IOfficeInfoWritePayload,
): IOfficeInfoWritePayload => ({
  officeType: office.office_info?.officeType,
  timePerPatientRDH: office.office_info?.timePerPatientRDH,
  timePerPatientDA: office.office_info?.timePerPatientDA,
  timePerPatientDN: office.office_info?.timePerPatientDN,
  isAssistedHygiene: office.office_info?.isAssistedHygiene,
  ...changes,
});

/**
 * Only the fields the office changed: a field it did not touch is never sent,
 * so an empty value in the record can't overwrite the stored one.
 */
export const buildBasicInformationPatch = (
  changes: Partial<Pick<IBasicInformationValues, 'officeName' | 'officeType'>>,
  office: IUser,
): IUpdateOfficeInfoApiPayload => ({
  ...(changes.officeName !== undefined && { office_name: changes.officeName }),
  ...(changes.officeType !== undefined && {
    office_info: editableOfficeInfo(office, { officeType: changes.officeType }),
  }),
});

export const buildContactPatch = (values: IContactValues): IUpdateOfficeInfoApiPayload => ({
  web_site: values.website,
});

/** Only the fields the office changed, as for Basic Information. */
export const buildShiftDetailsPatch = (
  changes: Partial<IShiftDetailsValues>,
  office: IUser,
): IUpdateOfficeInfoApiPayload => {
  const { notes, isAssistedHygiene, ...timePerPatient } = changes;
  const officeInfoChanges: IOfficeInfoWritePayload = {
    ...timePerPatient,
    ...(typeof isAssistedHygiene === 'boolean' && { isAssistedHygiene }),
  };
  return {
    ...(notes !== undefined && { notes_for_temp: notes }),
    ...(Object.keys(officeInfoChanges).length > 0 && {
      office_info: editableOfficeInfo(office, officeInfoChanges),
    }),
  };
};

export const buildPartnerReferralPatch = (
  organization: string,
  code: string,
): IUpdateOfficeInfoApiPayload => ({
  partnerReferral: { code, organization },
});

/**
 * The full productivity object `PATCH /users/office-productivity` stores, built
 * from the record and the edited parts — the legacy save also sent all of it.
 */
export const buildProductivityPatch = (
  office: IUser,
  changes: {
    operatories?: string | null;
    permanentStaff?: Record<TStaffProfession, string | null>;
    bookedOutPatients?: string | null;
  },
): IOfficeProductivityPayload => {
  const stored = office.office_info?.productivity;
  return {
    operatories:
      changes.operatories !== undefined ? changes.operatories : stored?.operatories || null,
    permanentStaff: changes.permanentStaff ?? {
      RDH: stored?.permanentStaff?.RDH || null,
      DA: stored?.permanentStaff?.DA || null,
      DN: stored?.permanentStaff?.DN || null,
    },
    bookedOutPatients:
      changes.bookedOutPatients !== undefined
        ? changes.bookedOutPatients
        : stored?.bookedOutPatients || null,
  };
};

/**
 * The record as it reads after a successful `PATCH /users` with `patch`, so
 * the page and the switcher show the new values before the refetch lands.
 */
export const applyOfficePatch = (office: IUser, patch: IUpdateOfficeInfoApiPayload): IUser => {
  const { partnerReferral, office_info: officeInfo, parkingInformation, ...topLevel } = patch;
  return {
    ...office,
    ...topLevel,
    ...(officeInfo && { office_info: { ...office.office_info, ...officeInfo } }),
    ...(parkingInformation && {
      parkingInformation: { ...office.parkingInformation, ...parkingInformation },
    }),
    ...(partnerReferral && {
      partnerReferral: { ...office.partnerReferral, partnershipCode: partnerReferral },
    }),
  } as IUser;
};

/** The record as it reads after a successful productivity save. */
export const applyProductivity = (office: IUser, productivity: IOfficeProductivityPayload): IUser =>
  ({
    ...office,
    office_info: { ...office.office_info, productivity },
  }) as IUser;
